import { LitElement, html, TemplateResult, nothing } from 'lit';
import { property, customElement, state, query, queryAsync } from 'lit/decorators.js';
import { styles } from '../view/styles';
import {
  handleWatchlistChangeButtonClick,
  overlayOpenListener,
  renderAccordionHeader,
  renderButtonGroup,
  renderLink,
  renderLastScannedOn,
  renderSideMenuCompanyIfNeeded,
  renderSideMenuOperatingSystem,
  renderSideMenuRiskScore,
  renderSideMenuSectionTitle,
  renderSideMenuState,
  renderSideMenuStatus,
  renderSideMenuWatchlist,
  RiskManagementSideMenuEvents,
  SideMenuButton,
} from '../../common/common';
import { translateFn } from '../../../../utils/translator.helper';
import { FontSizes, FontWeights } from '../../../../types/common/types';
import { BaseWebComponentTranslator } from '../../../../framework/BaseWebComponentTranslator';
import {
  AffectedDeviceType,
  AppIds,
  DetailsType,
  DEVICES_CLASS,
  IsolationStatus,
  MitigationType,
  RiskTypes,
  State,
  LocalResourcesType,
  CVE_SIDE_PANEL_SHOW_LIMIT,
  CVE_SIDE_PANEL_SHOW_INCREMENT,
  PROVIDER_STR_CLASS,
} from '../../../common/constants';
import {
  formatMacAddress,
  getBulletColorForScore,
  getPatchButtonDetails,
  getWatchlistButtonTextBasedOnState,
  isPatchManagementDetailsVisible,
  isCloudResource
} from '../../../common/utils';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { ConsoleRequest, DataProviderType } from '../../../../utils/request';
import { GzTabChangeEvent } from '../../../../ui/gzTabs/gzTabGroup/events/GzTabChangeEvent';
import moment from 'moment/moment';
import { GzButtonWebComponent } from '../../../../ui/gzButton';
import { DeviceExtraDetails } from '../../components/deviceDetails/types/types';
import { ContextMenuOverlayElement } from '../../../../ui/gzContextMenuOverlay';
import { ImageState } from '../../../../ui/gzImageRepo/types';
import { ColumnKeys as DevicesColumnKeys } from '../../../devicesApp/types';
import { buildHash } from '@nirvana/shared/navigation';
import { Feature } from '@nirvana/shared/constants';

/**
 * @summary Type here the description of this component
 * @tag devices-side-menu
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('devices-side-menu')
export class DevicesSideMenuWebComponent extends LitElement {
  @property({ type: Object, attribute: false })
  item = {} as any;

  @property({ type: Object, attribute: false })
  tabsDetails = null as null | any;

  @property({ type: Object, attribute: false })
  patchManagementDetails = null as null | any;

  @property({ type: Object, attribute: false })
  deviceDetails = null as null | any;

  @property({ type: Boolean, attribute: false })
  deviceDetailsHasErrors = false;

  @property({ type: Object, attribute: false })
  deviceExtraDetails = null as null | DeviceExtraDetails;

  @property({ type: Boolean, attribute: true, reflect: true })
  showExtraDetailsPanel = false;

  @property({ type: String, attribute: false })
  extraDetailsPanelTitle = null as string | null;

  @state()
  showIgnoredRisks = true;

  @state()
  showIgnoredApps = true;

  @state()
  searchedValue = '';

  @state()
  cveDetails: any;

  @state()
  selectedApplicationName: string | null;

  @state()
  tabSelected = 0;

  @query('#watchlist-button')
  private readonly watchlistButton!: GzButtonWebComponent;

  @query('#extra-details-context-menu-side-panel')
  private readonly overlay!: ContextMenuOverlayElement;

  riskType = RiskTypes.DEVICES;

  // see https://lit-element.polymer-project.org/guide/styles
  static get styles() {
    return styles;
  }

  render() {
    return html`
      ${this.renderSideMenuHeader()}

      <div class="content flex-1">${this.renderSideMenuContent()}</div>
      <gz-context-menu-overlay id="extra-details-context-menu-side-panel" @openContextMenu="${overlayOpenListener}">
      </gz-context-menu-overlay>
    `;
  }

  public changeWatchlistButtonState(isLoading: boolean) {
    this.watchlistButton.setLoadingState(isLoading);
  }

  private renderSideMenuHeader() {
    if (this.showExtraDetailsPanel) {
      return html`${this.renderExtraDetailsPanelHeader()}`;
    }

    if (this.selectedApplicationName) {
      return html`${this.renderSelectedApplicationPanelHeader()}`;
    }

    return html` <div class="header mt-6">
      <gz-title fontSize="${FontSizes.HEADING_5}" class="mb-2"> ${this.item.endpointName}</gz-title>
    </div>`;
  }

  private renderSelectedApplicationPanelHeader() {
    return html`
      <div class="header mt-2">
        <gz-button
          class="backBtn"
          type="quiet"
          color="secondary"
          buttonheight="small"
          buttonfontsize="small"
          @click="${this.handleBackButtonClick}"
        >
          <gz-icon class="backBtn-icon" color="secondary" size="24" name="ds-icon-arrow_left_collapse"></gz-icon>
          ${translateFn('Back')}
        </gz-button>
        <gz-title fontSize="${FontSizes.HEADING_5}" class="mb-2 mt-3"> ${this.selectedApplicationName}</gz-title>
      </div>
    `;
  }

  private renderExtraDetailsPanelHeader() {
    return html`
      <div class="header mt-2">
        <gz-button
          class="backBtn"
          type="quiet"
          color="secondary"
          buttonheight="small"
          buttonfontsize="small"
          @click="${this.handleDeviceExtraDetailsBackButtonClick}"
        >
          <gz-icon class="backBtn-icon" color="secondary" size="24" name="ds-icon-arrow_left_collapse"></gz-icon>
          ${translateFn('Back')}
        </gz-button>
        <gz-title fontSize="${FontSizes.HEADING_5}" class="mb-2 mt-3"> ${this.extraDetailsPanelTitle}</gz-title>
      </div>
    `;
  }

  private renderSideMenuContent() {
    if (this.showExtraDetailsPanel) {
      return html`
        <device-extra-details
          .itemsType="${this.deviceExtraDetails?.itemsType}"
          .items="${this.deviceExtraDetails?.items}"
          .companyId="${this.deviceExtraDetails?.companyId}"
        >
        </device-extra-details>
      `;
    }

    if (this.selectedApplicationName) {
      return html` <gz-application-cve-list
        .isShowMorePresent="${true}"
        .showMoreLimit="${CVE_SIDE_PANEL_SHOW_LIMIT}"
        .increment="${CVE_SIDE_PANEL_SHOW_INCREMENT}"
      >
      </gz-application-cve-list>`;
    }

    return html`${this.renderGeneralSection()} ${this.renderRelatedResourcesSection()} ${this.renderMitigationSection()} ${this.renderTabs()}`;
  }

  private renderGeneralSection(): TemplateResult {
    const isNotImageEndpointType = Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_BCST_IMAGE_SCANNER_USABLE])
      ? this.item.endpointType !== AffectedDeviceType.CONTAINER_IMAGE
      : true;
    
    return html`
      <div class="section pb-2" data-section="general">
        ${renderSideMenuSectionTitle(translateFn('General'))}
        ${renderSideMenuRiskScore(this.item, this.overlay, this.riskType)}
        ${this.renderEndpointType(this.item.endpointType)} ${this.renderResourceId(this.item.resourceId)}
        ${isNotImageEndpointType ? renderSideMenuOperatingSystem(this.item.operatingSystem) : nothing}
        ${this.renderRegion(this.item.region)}
        ${this.renderCloudAccountId(this.item.cloudAccount)}
        ${this.renderImageData()}
        ${this.renderFQDN(this.item.fqdn)} ${this.renderIp(this.item.ip)} ${this.renderMacs(this.item.macs)}
        ${this.renderTags(this.item.tags)}
        ${isNotImageEndpointType ? renderSideMenuStatus(this.item.status) : nothing}
        ${isNotImageEndpointType ? renderSideMenuState(this.item.state) : nothing}
        ${renderLastScannedOn(this.item.lastScan)}
        ${this.renderMisconfigurations(this.item.misconfigurationIds)} ${this.renderCVEsCount(this.item.cveCount)}
        ${renderSideMenuCompanyIfNeeded(this.item.company?.name)} ${renderSideMenuWatchlist(this.item.watchlist)}
        ${this.renderGeneralSectionButtons(this.item.company?.specifics?.features)}
      </div>
    `;
  }

  private renderGeneralSectionButtons(features) {
    const buttons: Array<SideMenuButton> = [];

    if (LocalResourcesType[this.item.endpointType]) {
      buttons.push({
        text: translateFn('View identities'),
        handler: this.handleViewUsersButtonClick,
        id: 'view-users-button',
      });
    }

    buttons.push({
      text: getWatchlistButtonTextBasedOnState(this.item.watchlist),
      handler: handleWatchlistChangeButtonClick.bind(this),
      id: 'watchlist-button',
    });

    if (LocalResourcesType[this.item.endpointType]) {
      buttons.push({
        text: translateFn('View events and alerts'),
        handler: this.handleViewEventsButtonClick,
        id: 'view-events-button',
        disabled: !features.edrUsable,
        tooltiptext: translateFn('This option requires a license that includes the Historical Search feature.'),
      });
    }

    if (
      this.item.operatingSystem !== AffectedDeviceType.K8S_NAMESPACE &&
      this.item.endpointType !== AffectedDeviceType.CONTAINER_IMAGE &&
      this.item.endpointType !== AffectedDeviceType.CONTAINER
    ) {
      buttons.push({
        text: translateFn('View incidents'),
        handler: this.handleViewIncidentsButtonClick,
        id: 'view-incidents-button',
        disabled: !features.edrVisible && !features.eliteVisible,
        tooltiptext: translateFn('This option requires a license that includes the EDR feature.'),
      });
    }

    return renderButtonGroup(buttons);
  }

  private renderRelatedResourcesSection() {
    if (this.item.operatingSystem === AffectedDeviceType.K8S_NAMESPACE) {
      return html`
        <div class="section pb-2 pt-1" data-section="related-resources">
          ${renderSideMenuSectionTitle(translateFn('Related resources'))}
          ${this.renderClusterName(this.item.kspmMetadata.clusterName)}
          ${this.renderNamespaceName(this.item.kspmMetadata.namespace)}
        </div>
      `;
    }

    if (this.item.endpointType === AffectedDeviceType.CONTAINER_IMAGE) {
      return html`
        <div class="section pb-2 pt-1" data-section="related-resources">
          ${renderSideMenuSectionTitle(translateFn('Related resources'))}
          ${this.renderRepositoryName(this.item.imageMetadata.imageRepo)}
        </div>
      `;
    }

    if (this.item.endpointType === AffectedDeviceType.CONTAINER) {
      return html`
        <div class="section pb-2 pt-1" data-section="related-resources">
          ${renderSideMenuSectionTitle(translateFn('Related resources'))}
          ${this.renderContainerHostName(this.item.hostName)}
          ${this.renderContainerImageName(this.item.imageName)}
          ${this.renderContainerImageDigest(this.item.imageDigest)}
        </div>
      `;
    }
  }

  private renderMitigationSection(): TemplateResult {
    const buttons: Array<SideMenuButton> = [];

    if (this.tabsDetails) {
      const getFixRisksStateAndTooltip = () => {
        if (!this.item.company.specifics.features.iorUsable) {
          return {
            disabled: true,
            tooltiptext: translateFn('Your current license does not provide access to this feature.'),
          };
        }

        const automaticMitigations = this.tabsDetails.misconfigurations.filter(
          (misconfiguration: any) => misconfiguration.mitigation === MitigationType.AUTOMATIC
        );
        if (isCloudResource(this.item.endpointType) || automaticMitigations.length === 0) {
          return {
            disabled: true,
            tooltiptext: translateFn(
              'Automatic mitigation is not applicable for any of the risks affecting the resource.'
            ),
          };
        }

        return {
          disabled: false,
        };
      };

      buttons.push({
        text: translateFn('Fix risks'),
        handler: this.handleFixRisksButtonClick,
        id: 'fix-risks-button',
        ...getFixRisksStateAndTooltip(),
      });

      const { disabledRollback, tooltipRollback } = this.getRollbackRisksButtonDisableState();
      buttons.push({
        text: translateFn('Roll back fixes'),
        handler: this.handleRollbackFixesButtonClick,
        disabled: disabledRollback,
        tooltiptext: tooltipRollback,
        id: 'rollback-fixes-button',
      });
    }

    if (this.item.state === State.IGNORED) {
      buttons.push({
        text: translateFn('Restore ignored resource'),
        handler: this.handleRestoreButtonClick,
        id: 'restore-device-button',
        disabled: !this.item.company.specifics.features.iorUsable,
        tooltiptext: translateFn('Your current license does not provide access to this feature.'),
      });
    } else {
      buttons.push({
        text: translateFn('Ignore resource'),
        handler: this.handleIgnoreButtonClick,
        id: 'ignore-device-button',
        disabled: !this.item.company.specifics.features.iorUsable,
        tooltiptext: translateFn('Your current license does not provide access to this feature.'),
      });
    }

    if (this.tabsDetails && LocalResourcesType[this.item.endpointType]) {
      buttons.push(this.getIsolateButtonDetails());
    }

    return html`
      <div class="section pb-2 pt-1 no-border" data-section="mitigation">
        ${renderSideMenuSectionTitle(translateFn('Risk mitigation'))} ${renderButtonGroup(buttons)}
      </div>
    `;
  }

  private getIsolateButtonDetails() {
    const isolated = [IsolationStatus.ISOLATION_STATUS_PENDING_RESTORE, IsolationStatus.ISOLATION_STATUS_ISOLATED];

    if (!this.item.company.specifics.features.isolationUsable) {
      return {
        text: isolated.includes(this.tabsDetails.isolationStatus.data.status)
          ? translateFn('Remove from isolation')
          : translateFn('Isolate'),
        handler: this.handleIsolateButtonClick,
        id: 'isolate-device-button',
        disabled: true,
        tooltiptext: translateFn("The company's license does not include this feature."),
      };
    } else {
      switch (this.tabsDetails.isolationStatus.data.status) {
        case IsolationStatus.ISOLATION_STATUS_ISOLATED:
          return {
            text: translateFn('Remove from isolation'),
            handler: this.handleRemoveFromIsolationButtonClick,
            id: 'remove-from-isolation-button',
          };
        case IsolationStatus.ISOLATION_STATUS_NOT_ISOLATED:
          return {
            text: translateFn('Isolate'),
            handler: this.handleIsolateButtonClick,
            id: 'isolate-device-button',
          };
        case IsolationStatus.ISOLATION_STATUS_PENDING_ISOLATION:
          return {
            text: translateFn('Isolate'),
            handler: this.handleIsolateButtonClick,
            id: 'isolate-device-button',
            disabled: true,
            tooltiptext: translateFn('Another isolation task is currently in progress.'),
          };
        case IsolationStatus.ISOLATION_STATUS_PENDING_RESTORE:
          return {
            text: translateFn('Remove from isolation'),
            handler: this.handleRemoveFromIsolationButtonClick,
            id: 'remove-from-isolation-button',
            disabled: true,
            tooltiptext: translateFn('Another restoration task is currently in progress.'),
          };
        default:
          return {
            text: translateFn('Isolate'),
            handler: this.handleIsolateButtonClick,
            id: 'remove-from-isolation-button',
            disabled: true,
            tooltiptext:
              this.item.appId !== AppIds.APPID_UNIFIED_CLIENT
                ? translateFn('The target resource is not an endpoint or does not have the EDR Sensor installed.')
                : translateFn('Action unavailable.'),
          };
      }
    }
  }

  private renderTabs() {
    if (!this.tabsDetails) {
      return html` <gz-loader></gz-loader>`;
    }

    if (this.item.operatingSystem === AffectedDeviceType.K8S_NAMESPACE) {
      return html`
        <gz-tab-group @gzTabChange="${this.handleTabChange}" fillSpace noWrap>
          ${this.renderMisconfigurationsTab()}
        </gz-tab-group>
      `;
    }

    if (
      this.item.endpointType === AffectedDeviceType.CONTAINER_IMAGE ||
      this.item.endpointType === AffectedDeviceType.CONTAINER
    ) {
      return html`
        <gz-tab-group @gzTabChange="${this.handleTabChange}" fillSpace noWrap>
          ${this.renderMisconfigurationsTab()} ${this.renderAppVulnerabilitiesTab()}
        </gz-tab-group>
      `;
    }

    return !LocalResourcesType[this.item.endpointType]
      ? html`
          <gz-tab-group @gzTabChange="${this.handleTabChange}" fillSpace noWrap>
            ${this.renderMisconfigurationsTab()}
          </gz-tab-group>
        `
      : html`
          <gz-tab-group @gzTabChange="${this.handleTabChange}" fillSpace noWrap>
            ${this.renderMisconfigurationsTab()} ${this.renderAppVulnerabilitiesTab()}
          </gz-tab-group>
        `;
  }

  private renderCloudAccountId(cloudAccountId?: string) {
    return !cloudAccountId
      ? nothing :
      html`
        <gz-labeled-content label="${translateFn('Cloud account ID')}">
          <div class="w-full d-flex align-items-center" style="margin-top: -4px;">
            <gz-tooltip
              class="flex-1"
              isEllipsis
              tooltipText="${cloudAccountId}"
            >
              <span>${cloudAccountId}</span>
            </gz-tooltip>
            <gz-copy-to-clipboard
              class="copy-icon"
              confirmationMessage="${translateFn('Copied')}"
              tooltipText="${translateFn('Copy to clipboard')}"
              clipboardMessage="${cloudAccountId}"
              style="width: 36px; flex-shrink: 0;"
            ></gz-copy-to-clipboard>
          </div>
        </gz-labeled-content>
      `;
  }

  private renderRepositoryName(repositoryName?: string) {
    return html`
      <gz-labeled-content label="${translateFn('Repository')}">
        ${!repositoryName ? '-' : html`
          <div class="w-full d-flex align-items-center" style="margin-top: -4px;">
            <gz-tooltip
              class="flex-1"
              isEllipsis
              tooltipText="${repositoryName}"
            >
              <span>${repositoryName}</span>
            </gz-tooltip>
            <gz-copy-to-clipboard
              class="copy-icon"
              confirmationMessage="${translateFn('Copied')}"
              tooltipText="${translateFn('Copy to clipboard')}"
              clipboardMessage="${repositoryName}"
              style="width: 36px; flex-shrink: 0;"
            ></gz-copy-to-clipboard>
          </div>
        `}
      </gz-labeled-content>
    `;
  }
  
  private renderClusterName(clusterName?: string) {
    return html`
      <gz-labeled-content label="${translateFn('Cluster name')}">
        ${!clusterName ? '-' : html`
          <div class="w-full d-flex align-items-center" style="margin-top: -4px;">
            <gz-tooltip
              class="flex-1"
              isEllipsis
              tooltipText="${clusterName}"
            >
              <span>${clusterName}</span>
            </gz-tooltip>
            <gz-copy-to-clipboard
              class="copy-icon"
              confirmationMessage="${translateFn('Copied')}"
              tooltipText="${translateFn('Copy to clipboard')}"
              clipboardMessage="${clusterName}"
              style="width: 36px; flex-shrink: 0;"
            ></gz-copy-to-clipboard>
          </div>
        `}
      </gz-labeled-content>
    `;
  }

  private renderNamespaceName(namespaceName?: string) {
    return html`
      <gz-labeled-content label="${translateFn('Namespace name')}">
        ${!namespaceName ? '-' : html`
          <div class="w-full d-flex align-items-center" style="margin-top: -4px;">
            <gz-tooltip
              class="flex-1"
              isEllipsis
              tooltipText="${namespaceName}"
            >
              <span>${namespaceName}</span>
            </gz-tooltip>
            <gz-copy-to-clipboard
              class="copy-icon"
              confirmationMessage="${translateFn('Copied')}"
              tooltipText="${translateFn('Copy to clipboard')}"
              clipboardMessage="${namespaceName}"
              style="width: 36px; flex-shrink: 0;"
            ></gz-copy-to-clipboard>
          </div>
        `}
      </gz-labeled-content>
    `;
  }

  private renderContainerHostName(hostName?: string) {
    return html`
      <gz-labeled-content label="${translateFn('Container host')}">
        ${!hostName ? '-' : html`
          <div class="w-full d-flex align-items-center" style="margin-top: -4px;">
            <gz-tooltip
              class="flex-1"
              isEllipsis
              tooltipText="${hostName}"
            >
              <span>${hostName}</span>
            </gz-tooltip>
            <gz-copy-to-clipboard
              class="copy-icon"
              confirmationMessage="${translateFn('Copied')}"
              tooltipText="${translateFn('Copy to clipboard')}"
              clipboardMessage="${hostName}"
              style="width: 36px; flex-shrink: 0;"
            ></gz-copy-to-clipboard>
          </div>
        `}
      </gz-labeled-content>
    `;
  }

  private renderContainerImageName(imageName?: string) {
    return html`
      <gz-labeled-content label="${translateFn('Container image')}">
        ${!imageName ? '-' : html`
          <div class="w-full d-flex align-items-center" style="margin-top: -4px;">
            <gz-tooltip
              class="flex-1"
              isEllipsis
              tooltipText="${imageName}"
            >
              <span>${imageName}</span>
            </gz-tooltip>
            <gz-copy-to-clipboard
              class="copy-icon"
              confirmationMessage="${translateFn('Copied')}"
              tooltipText="${translateFn('Copy to clipboard')}"
              clipboardMessage="${imageName}"
              style="width: 36px; flex-shrink: 0;"
            ></gz-copy-to-clipboard>
          </div>
        `}
      </gz-labeled-content>
    `;
  }

  private renderContainerImageDigest(imageDigest?: string) {
    return html`
      <gz-labeled-content label="${translateFn('Container image ID')}">
        ${!imageDigest ? '-' : html`
          <div class="w-full d-flex align-items-center" style="margin-top: -4px;">
            <gz-tooltip
              class="flex-1"
              isEllipsis
              tooltipText="${imageDigest}"
            >
              <span>${imageDigest}</span>
            </gz-tooltip>
            <gz-copy-to-clipboard
              class="copy-icon"
              confirmationMessage="${translateFn('Copied')}"
              tooltipText="${translateFn('Copy to clipboard')}"
              clipboardMessage="${imageDigest}"
              style="width: 36px; flex-shrink: 0;"
            ></gz-copy-to-clipboard>
          </div>
        `}
      </gz-labeled-content>
    `;
  }

  private renderEndpointType(endpointType: AffectedDeviceType) {
    return html` <gz-labeled-content label="${translateFn('Resource type')}">
      <div class="entities-container d-flex flex-row" style="gap: 4px;">
        <gz-breach-path-graph-icon
          name="${DEVICES_CLASS.get(endpointType)}"
          color="var(--chart-302-icon-buttons-titles)"
        ></gz-breach-path-graph-icon>
        <gz-tooltip isEllipsis tooltipText="${BaseWebComponentTranslator.getInstance().getDeviceTypes(endpointType)}">
          <gz-text class="d-inline" fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.NORMAL}"
            >${BaseWebComponentTranslator.getInstance().getDeviceTypes(endpointType)}
          </gz-text>
        </gz-tooltip>
      </div>
    </gz-labeled-content>`;
  }

  private renderFQDN(fqdn: string) {
    return !LocalResourcesType[this.item.endpointType] && this.item.endpointType !== AffectedDeviceType.CONTAINER
      ? nothing
      : html` <gz-labeled-content label="${translateFn('FQDN')}"> ${fqdn}</gz-labeled-content>`;
  }

  private renderIp(ip: string) {
    return !LocalResourcesType[this.item.endpointType] && this.item.endpointType !== AffectedDeviceType.CONTAINER
      ? nothing
      : html` <gz-labeled-content label="${translateFn('IP')}"> ${ip}</gz-labeled-content>`;
  }

  private renderMacs(macs: Array<string>) {
    return !LocalResourcesType[this.item.endpointType] && this.item.endpointType !== AffectedDeviceType.CONTAINER
      ? nothing
      : html` <gz-labeled-content label="${translateFn('MAC')}">
          ${!macs || macs.length === 0 ? '-' : macs.map(mac => formatMacAddress(mac)).join(', ')}
        </gz-labeled-content>`;
  }

  private renderTags(tags: Array<string>) {
    return !LocalResourcesType[this.item.endpointType]
      ? nothing
      : html` <gz-labeled-content label="${translateFn('Endpoint tags')}">
          ${!tags || tags.length === 0 ? '-' : tags.join(', ')}
        </gz-labeled-content>`;
  }

  private renderResourceId(resourceId: string) {
    const resourceIdValue = !resourceId ? '-' : resourceId;

    return LocalResourcesType[this.item.endpointType] || this.item.endpointType === AffectedDeviceType.CONTAINER
      ? nothing
      : html` <gz-labeled-content label="${translateFn('Resource ID')}">
          <div class="entities-container d-flex flex-row">
            <gz-tooltip isEllipsis tooltipText="${resourceIdValue}">
              <gz-text class="d-inline" fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.NORMAL}"
                >${resourceIdValue}
              </gz-text>
            </gz-tooltip>
          </div>
        </gz-labeled-content>`;
  }

  private renderMisconfigurations(misconfigurationIds: Array<number>) {
    return html` <gz-labeled-content label="${translateFn('Findings')}">
      ${misconfigurationIds.length}
    </gz-labeled-content>`;
  }

  private renderCVEsCount(cveCount: number) {
    return !cveCount
      ? nothing
      : html` <gz-labeled-content label="${translateFn('CVEs')}"> ${cveCount}</gz-labeled-content>`;
  }

  private renderMisconfigurationsTab(): TemplateResult {
    return html` <gz-tab
      id="misconfiguration-tab"
      label="${translateFn('Findings')}"
      .selected="${this.tabSelected === 0}"
    >
      <div class="section pb-2 pt-1" data-section="misconfigurations">
        <gz-toggle .checked="${this.showIgnoredRisks}" @toggleChange="${this.handleIgnoredRisksToggleChange}">
          ${translateFn('Show ignored risks')}
        </gz-toggle>
        ${this.renderMisconfigurationsSection()}
      </div>
    </gz-tab>`;
  }

  private renderMisconfigurationsSection() {
    const allMisconfigurations = this.tabsDetails.misconfigurations;

    // No misconfigurations at all
    if (allMisconfigurations.length === 0) {
      return html`
        <gz-image-repo state="${ImageState.SUCCESS}">
          <gz-title fontWeight="${FontWeights.MEDIUM}" fontSize='${FontSizes.HEADING_4}'>${translateFn('No risks found')}</gz-title>
          <gz-text fontSize="13" fontWeight="${FontWeights.NORMAL}">
            ${translateFn('There are no risks associated with this entity.')}
          </gz-text>
        </gz-image-repo>
      `;
    }

    if (!this.showIgnoredRisks) {
      const activeMisconfigurations = allMisconfigurations.filter((misconfiguration: any) => {
        return misconfiguration['state'] === State.ACTIVE;
      });

      // We have only ignored misconfigurations and we hid them
      if (activeMisconfigurations.length === 0) {
        return html`
          <gz-image-repo state="${ImageState.EMPTY}">
            <gz-title fontWeight="${FontWeights.MEDIUM}" fontSize='${FontSizes.HEADING_4}'>${translateFn('No results found')}</gz-title>
            <gz-text fontSize="13" fontWeight="${FontWeights.NORMAL}">
              ${translateFn('Adjust your filter selection to view the ignored risks.')}
            </gz-text>
          </gz-image-repo>
        `;
      }
    }

    const automaticMisconfigurations = this.tabsDetails.misconfigurations.filter(
      (misconfiguration: any) => misconfiguration.mitigation === MitigationType.AUTOMATIC
    );
    const manualMisconfigurations = this.tabsDetails.misconfigurations.filter(
      (misconfiguration: any) => misconfiguration.mitigation === MitigationType.MANUAL
    );

    const { count: countAutomatic, template: automaticTemplate } = this.renderMisconfigurationsCategory(
      automaticMisconfigurations,
      true
    );
    const { count: countManual, template: manualTemplate } = this.renderMisconfigurationsCategory(
      manualMisconfigurations,
      false
    );

    return html`
      ${renderSideMenuSectionTitle(translateFn('Automatic mitigation') + ` (${countAutomatic})`)}
      <div class="pb-2">${automaticTemplate}</div>
      ${renderSideMenuSectionTitle(translateFn('Manual mitigation') + ` (${countManual})`)}
      <div class="pb-2">${manualTemplate}</div>
    `;
  }

  private renderNoRisksFoundMessage() {
    return html` <gz-text class="message pl-1 pb-1" fontSize="12" fontWeight="${FontWeights.LIGHT}">
      ${translateFn('No risks found')}
    </gz-text>`;
  }

  private renderNoActiveRisksMessage() {
    return html` <gz-text class="message pl-1 pb-1" fontSize="12" fontWeight="${FontWeights.LIGHT}">
      ${translateFn('Adjust your filter to view the ignored risks.')}
    </gz-text>`;
  }

  private renderMisconfigurationsCategory(misconfigurations: Array<any>, automatic: boolean) {
    // We do not have misconfigurations
    if (misconfigurations.length === 0) {
      return {
        template: this.renderNoRisksFoundMessage(),
        count: misconfigurations.length,
      };
    }

    // We want to show all misconfigurations
    if (this.showIgnoredRisks) {
      return {
        template: this.renderMisconfigurationsList(misconfigurations, automatic),
        count: misconfigurations.length,
      };
    }

    const activeMisconfigurations = misconfigurations.filter(
      (misconfiguration: any) => misconfiguration.state === State.ACTIVE
    );

    // We have only ignored misconfigurations and we hid them
    if (activeMisconfigurations.length === 0) {
      return {
        template: this.renderNoActiveRisksMessage(),
        count: activeMisconfigurations.length,
      };
    }

    return {
      template: this.renderMisconfigurationsList(activeMisconfigurations, automatic),
      count: activeMisconfigurations.length,
    };
  }

  private renderMisconfigurationsList(misconfigurations: any, automatic: boolean) {
    const result: any = [];

    for (const misconfiguration of misconfigurations) {
      const { deviceExtraDetails, hasEmptyState } = this.getExtraDetails(
        misconfiguration['_id'],
        DetailsType.MISCONFIGURATION
      );

      const copyToClipboard = {
        enabled: true,
        text: Application.modules.riskManagement.controller.helpers.routinesTranslations.translateRoutineName(
          misconfiguration['indicatorId']
        ),
      };

      const template: TemplateResult = html`
        <gz-accordion align="left" .copyToClipboard="${copyToClipboard}">
          ${renderAccordionHeader(
            Application.modules.riskManagement.controller.helpers.routinesTranslations.translateRoutineName(
              misconfiguration['indicatorId']
            ),
            misconfiguration['scoreBreakdown']['finalScore'],
            misconfiguration['state']
          )}
          ${this.renderMisconfigurationDetailsSection(misconfiguration, automatic)}
          ${this.renderDeviceDetailsSection(
            deviceExtraDetails,
            hasEmptyState,
            Application.modules.riskManagement.controller.helpers.routinesTranslations.translateRoutineName(
              misconfiguration['indicatorId']
            )
          )}
        </gz-accordion>
      `;

      result.push(template);
    }

    return result;
  }

  private renderMisconfigurationDetailsSection(misconfiguration, automatic: boolean) {
    const actions: any = [];

    if (automatic) {
      actions.push(
        renderLink(
          translateFn('Fix risk'),
          () => {
            this.handleFixRiskButtonClick(
              misconfiguration['indicatorId'],
              this.item.endpointId,
              this.item.company._id,
              this.item.hasFrat
            );
          },
          false,
          !this.item.company.specifics.features.iorUsable,
          !this.item.company.specifics.features.iorUsable
            ? translateFn('Your current license does not provide access to this feature.')
            : ''
        )
      );
    }

    actions.push(
      renderLink(
        translateFn('View risk details'),
        () => {
          this.handleViewRiskDetailsButtonClick(misconfiguration['indicatorId'], this.item.company._id);
        },
        automatic // has separator only if before there is another element. This happens only when mitigation is automatic
      ),
      renderLink(
        translateFn('View resources'),
        () => {
          this.handleViewOtherDevicesByMisconfigurationButtonClick(
            misconfiguration['indicatorId'],
            this.item.company._id
          );
        },
        true
      )
    );

    return html` <div class="pb-1">
      <gz-text class="text-description pb-2 pt-1" fontSize="${FontSizes.PARAGRAPH_12}">
        ${unsafeHTML(
          Application.modules.riskManagement.controller.helpers.routinesTranslations.translateRoutineDescription(
            misconfiguration['indicatorId']
          )
        )}
      </gz-text>
      ${this.renderRiskScore(misconfiguration, RiskTypes.MISCONFIGURATIONS)}
      <gz-labeled-content label="${translateFn('State')}">
        ${BaseWebComponentTranslator.getInstance().getRiskManagementState(misconfiguration['state'])}
      </gz-labeled-content>
      <gz-labeled-content label="${translateFn('Risk mitigation')}"></gz-labeled-content>
      <gz-text class="text-description pb-1" fontSize="${FontSizes.PARAGRAPH_12}">
        ${unsafeHTML(
          Application.modules.riskManagement.controller.helpers.routinesTranslations.translateRoutineAction(
            misconfiguration['indicatorId']
          )
        )}
      </gz-text>
      <div class="d-flex flex-row align-items-center link-container pt-1">${actions}</div>
    </div>`;
  }

  private renderRiskScore(item, scoreBreakdownType) {
    if (typeof item['scoreBreakdown'] === 'undefined') {
      return nothing;
    }

    return renderSideMenuRiskScore(item, this.overlay, scoreBreakdownType);
  }

  private renderApplicationsList(filteredApplications: any) {
    const result: any = [];

    for (const application of filteredApplications) {
      const { deviceExtraDetails, hasEmptyState } = this.getExtraDetails(
        application['_id'],
        DetailsType.VULNERABILITIES
      );

      const copyToClipboard = {
        enabled: true,
        text: application['fullApplicationName'],
      };

      const template: TemplateResult = html`
        <gz-accordion align="left" .copyToClipboard="${copyToClipboard}">
          ${renderAccordionHeader(
            application['fullApplicationName'],
            application['scoreBreakdown']['finalScore'],
            application['state']
          )}
          ${this.renderApplicationDetailsSection(application)}
          ${this.renderDeviceDetailsSection(deviceExtraDetails, hasEmptyState, application['fullApplicationName'])}
        </gz-accordion>
      `;

      result.push(template);
    }

    if (filteredApplications.length === 0) {
      result.push(html`
        <gz-image-repo state="${ImageState.EMPTY}">
          <gz-title fontWeight="${FontWeights.MEDIUM}" fontSize='${FontSizes.HEADING_4}'>${translateFn('No results found')}</gz-title>
          <gz-text fontSize="13" fontWeight="${FontWeights.NORMAL}">
            ${translateFn('Adjust your filter and try again.')}
          </gz-text>
        </gz-image-repo>
      `);
    }

    return html`
      ${renderSideMenuSectionTitle(
        translateFn('Applications with missing patches') + ` (${filteredApplications.length})`
      )}
      <gz-input
        class="pb-2"
        value="${this.searchedValue}"
        placeholder=${translateFn('CVE code')}
        @change="${this.handleCveIdInputChange}"
      ></gz-input>
      ${result}
    `;
  }

  private renderApplications(applications) {
    if (applications.length === 0) {
      return html`
        <gz-image-repo state="${ImageState.SUCCESS}">
          <gz-title fontWeight="${FontWeights.MEDIUM}" fontSize='${FontSizes.HEADING_4}'
            >${translateFn('No vulnerabilities found')}
          </gz-title>
          <gz-text fontSize="13" fontWeight="${FontWeights.NORMAL}">
            ${translateFn('There are no vulnerabilities associated with this resource.')}
          </gz-text>
        </gz-image-repo>
      `;
    }

    if (!this.searchedValue && !this.showIgnoredApps) {
      const activeApps = applications.filter(application => application.state === State.ACTIVE);

      if (activeApps.length === 0) {
        return html`
          <gz-image-repo state="${ImageState.EMPTY}">
            <gz-title fontWeight="${FontWeights.MEDIUM}" fontSize='${FontSizes.HEADING_4}'>${translateFn('No results found')}</gz-title>
            <gz-text fontSize="13" fontWeight="${FontWeights.NORMAL}">
              ${translateFn('Adjust your filter to view the ignored vulnerabilities.')}
            </gz-text>
          </gz-image-repo>
        `;
      }
    }

    const searchedApplications = this.tabsDetails.vulnerabilities.filter(application => {
      return application['cves'].some(
        (cve: string) =>
          cve.toLowerCase().includes(this.searchedValue.toLowerCase()) &&
          (this.showIgnoredApps || application['state'] !== State.IGNORED)
      );
    });

    return this.renderApplicationsList(searchedApplications);
  }

  private renderApplicationDetailsSection(application) {
    let patchLinkDisabled = false;
    let patchLinkTooltiptext = '';

    let showPatchInformation = false;
    if (this.patchManagementDetails && this.patchManagementDetails[application['_id']]) {
      showPatchInformation = isPatchManagementDetailsVisible(this.patchManagementDetails[application['_id']]);

      if (showPatchInformation) {
        const { patchButtonDisabled, patchButtonTooltiptext } = getPatchButtonDetails(
          this.patchManagementDetails[application['_id']]
        );
        patchLinkDisabled = patchButtonDisabled;
        patchLinkTooltiptext = patchButtonTooltiptext;
      }
    }

    return html`
      <div>
        <gz-text class="text-description pb-1" fontSize="${FontSizes.PARAGRAPH_12}">
          ${showPatchInformation
            ? translateFn(
                'Apply all patches for {APP_NAME} to mitigate {NUMBER_OF_CVES} known vulnerabilities affecting your endpoints.',
                {
                  APP_NAME: application.fullApplicationName,
                  NUMBER_OF_CVES: application.cves.length,
                }
              )
            : nothing}
        </gz-text>
        ${this.renderRiskScore(application, RiskTypes.VULNERABILITIES)}
        <gz-labeled-content label="${translateFn('State')}">
          ${BaseWebComponentTranslator.getInstance().getRiskManagementState(application['state'])}
        </gz-labeled-content>
        <gz-labeled-content label="${translateFn('CVEs')}">
          ${renderLink(application.cves.length, async () => {
            await this.handleCveCountButtonClick(application);
          })}
        </gz-labeled-content>
      </div>
      <div class="d-flex flex-row align-items-center link-container pt-1 pb-1">
        ${showPatchInformation
          ? renderLink(
              translateFn('Patch app'),
              () => {
                this.handlePatchAppButtonClick(application);
              },
              false,
              patchLinkDisabled,
              patchLinkDisabled ? patchLinkTooltiptext : ''
            )
          : nothing}
        ${renderLink(
          translateFn('View resources'),
          this.composeViewDevicesByCveLink(application),
          showPatchInformation
        )}
      </div>
    `;
  }

  private renderDeviceDetailsSection(deviceExtraDetails, hasEmptyState: boolean, title: string) {
    if (hasEmptyState) {
      return nothing;
    }

    return html` <div class="section pb-2 pt-1" data-section="deviceDetails">
      <device-details
        .deviceInformation="${deviceExtraDetails}"
        .extraDetailsPanelTitle="${title}"
        .companyId="${this.item.company._id}"
        ?hasErrors="${this.deviceDetailsHasErrors}"
        ?emptyState="${hasEmptyState}"
      ></device-details>
    </div>`;
  }

  private getExtraDetails(itemId, itemType) {
    let deviceExtraDetails = null;
    let hasEmptyState = false;

    if (!this.deviceDetails) {
      return { deviceExtraDetails, hasEmptyState };
    }

    if (!this.deviceDetails[itemType][itemId]) {
      hasEmptyState = true;
    } else if (Object.keys(this.deviceDetails[itemType][itemId]).length === 0) {
      hasEmptyState = true;
    } else {
      deviceExtraDetails = this.deviceDetails[itemType][itemId];
    }

    return { deviceExtraDetails, hasEmptyState };
  }

  private renderAppVulnerabilitiesTab(): TemplateResult {
    const applications = this.tabsDetails.vulnerabilities;

    return html` <gz-tab
      id="applications-tab"
      label="${translateFn('App vulnerabilities')}"
      .selected="${this.tabSelected === 1}"
    >
      <div class="section pb-2 pt-1">
        <gz-toggle .checked="${this.showIgnoredApps}" @toggleChange="${this.handleIgnoredAppsToggleChange}">
          ${translateFn('Show ignored applications')}
        </gz-toggle>
        ${this.renderApplications(applications)}
      </div>
    </gz-tab>`;
  }

  private renderRegion(region: String) {
    return !region
      ? nothing
      : html` <gz-labeled-content label="${translateFn('Region')}">
          <div class="entities-container d-flex flex-row">
            <gz-tooltip isEllipsis tooltipText="${region}">
              <gz-text class="d-inline" fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.NORMAL}"
                >${region}
              </gz-text>
            </gz-tooltip>
          </div>
        </gz-labeled-content>`;
  }

  private renderImageData() {
    return Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_BCST_IMAGE_SCANNER_USABLE])
      && this.item.endpointType === AffectedDeviceType.CONTAINER_IMAGE
        ? html`
            <gz-labeled-content label="${translateFn('Registry host')}">
              <div class="entities-container d-flex align-items-center flex-row" style="gap: 4px;">
                <gz-breach-path-graph-icon
                  name="${PROVIDER_STR_CLASS.get(this.item.imageMetadata?.cloudProvider)}"
                  color="var(--chart-302-icon-buttons-titles)"
                ></gz-breach-path-graph-icon>
                <gz-tooltip isEllipsis tooltipText="${this.item.imageMetadata?.registryHost}">
                  <gz-text class="d-inline" fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.NORMAL}">
                    ${this.item.imageMetadata?.registryHost || '-'}
                  </gz-text>
                </gz-tooltip>
              </div>
            </gz-labeled-content>
            <gz-labeled-content label="${translateFn('OS/Architecture')}">
              <div class="entities-container d-flex flex-row">
                ${this.item.imageMetadata?.imageArchitecture || '-'}
              </div>
            </gz-labeled-content>
            <gz-labeled-content label="${translateFn('Image size')}">
              <div class="entities-container d-flex flex-row">
                ${this.item.imageMetadata?.imageSize
                  ? `${(this.item.imageMetadata.imageSize / 1000000).toFixed(2)} MB` 
                  : '-'}
              </div>
            </gz-labeled-content>
          `
        : nothing;
  }

  private handleTabChange(event: GzTabChangeEvent) {
    this.tabSelected = event.currentTab;
  }

  private handleIgnoredRisksToggleChange(event: CustomEvent) {
    this.showIgnoredRisks = event.detail.checked;
  }

  private handleIgnoredAppsToggleChange(event: CustomEvent) {
    this.showIgnoredApps = event.detail.checked;
  }

  private handleViewUsersButtonClick() {
    this.dispatchEvent(
      new CustomEvent(RiskManagementSideMenuEvents.VIEW_USERS_BY_DEVICE, {
        detail: {
          companyId: this.item.company._id,
          device: {
            label: this.item.endpointName.toLowerCase(),
            type: this.item.endpointType,
            value: this.item.endpointId,
          },
        },
        bubbles: true,
        cancelable: true,
        composed: true,
      })
    );
  }

  private handleIgnoreButtonClick(): void {
    this.dispatchEvent(
      new CustomEvent(RiskManagementSideMenuEvents.IGNORE_DEVICES, {
        detail: {
          companyId: this.item.company._id,
          endpointsIds: [this.item.endpointId],
        },
        bubbles: true,
        cancelable: true,
        composed: true,
      })
    );
  }

  private handleRestoreButtonClick(): void {
    this.dispatchEvent(
      new CustomEvent(RiskManagementSideMenuEvents.RESTORE_DEVICES, {
        detail: {
          companyId: this.item.company._id,
          endpointsIds: [this.item.endpointId],
        },
        bubbles: true,
        cancelable: true,
        composed: true,
      })
    );
  }

  private handleIsolateButtonClick(): void {
    this.dispatchEvent(
      new CustomEvent(RiskManagementSideMenuEvents.ISOLATE_DEVICE, {
        detail: {
          companyId: this.item.company._id,
          endpointId: this.item.endpointId,
          isolationStatus: this.tabsDetails.isolationStatus,
        },
        bubbles: true,
        cancelable: true,
        composed: true,
      })
    );
  }

  private handleRemoveFromIsolationButtonClick(): void {
    this.dispatchEvent(
      new CustomEvent(RiskManagementSideMenuEvents.REMOVE_FROM_ISOLATION, {
        detail: {
          companyId: this.item.company._id,
          endpointId: this.item.endpointId,
          isolationStatus: this.tabsDetails.isolationStatus,
        },
        bubbles: true,
        cancelable: true,
        composed: true,
      })
    );
  }

  private handleFixRiskButtonClick(indicatorId: string, endpointId: string, companyId: string, hasFrat = false) {
    this.dispatchEvent(
      new CustomEvent(RiskManagementSideMenuEvents.FIX_MISCONFIGURATIONS, {
        detail: {
          indicatorsIds: [indicatorId],
          companyId,
          endpointsIds: [endpointId],
          hasFrat,
        },
        bubbles: true,
        cancelable: true,
        composed: true,
      })
    );
  }

  private handleViewOtherDevicesByMisconfigurationButtonClick(indicatorId: number, companyId: string) {
    this.dispatchEvent(
      new CustomEvent(RiskManagementSideMenuEvents.VIEW_DEVICES_BY_MISCONFIGURATION, {
        detail: {
          indicatorId,
          companyId,
        },
        bubbles: true,
        cancelable: true,
        composed: false,
      })
    );
  }

  private handleCveIdInputChange(event: CustomEvent) {
    this.searchedValue = event.detail.sourceEvent;
  }

  private async handleCveCountButtonClick(application: any) {
    this.selectedApplicationName = application['fullApplicationName'];

    const result = await ConsoleRequest.createRequest<any>({
      dataProviderType: DataProviderType.CORE,
      endpoint: 'GlobalInventoryCve',
      method: 'getCVEsForApplication',
      payload: {
        applicationId: application['_id'],
      },
    });

    const component = this.shadowRoot?.querySelector('gz-application-cve-list');
    if (!component) {
      return;
    }

    component.companyId = this.item.company._id;
    component.searchedValue = this.searchedValue;

    const allCves = application.cves;

    const cveList = result ?? [];
    const cvesFromResponse: string[] = cveList.map((cve: { standardId: string }) => cve.standardId);
    for (const cve of allCves) {
      if (cvesFromResponse.indexOf(cve) === -1) {
        cveList.push({
          standardId: cve,
          hasMissingDetails: true,
        });
      }
    }
    component.cveList = cveList;
    component.hasViewDevicesButtonVisible = true;
  }

  private handleBackButtonClick() {
    this.selectedApplicationName = null;
  }

  private handleDeviceExtraDetailsBackButtonClick() {
    this.showExtraDetailsPanel = false;
  }

  private handleViewEventsButtonClick() {
    this.dispatchEvent(
      new CustomEvent(RiskManagementSideMenuEvents.VIEW_EVENTS_AND_ALERTS_BY_DEVICE, {
        detail: {
          endpointId: this.item.endpointId,
        },
        bubbles: true,
        cancelable: true,
        composed: false,
      })
    );
  }

  private handleViewIncidentsButtonClick() {
    !LocalResourcesType[this.item.endpointType]
      ? this.dispatchEvent(
          new CustomEvent(RiskManagementSideMenuEvents.VIEW_INCIDENTS_BY_DEVICE_CLOUD, {
            detail: {
              resourceName: this.item.endpointName,
              companyId: this.item.company._id,
            },
            bubbles: true,
            cancelable: true,
            composed: false,
          })
        )
      : this.dispatchEvent(
          new CustomEvent(RiskManagementSideMenuEvents.VIEW_INCIDENTS_BY_DEVICE, {
            detail: {
              endpointId: this.item.endpointId,
            },
            bubbles: true,
            cancelable: true,
            composed: false,
          })
        );
  }

  private handleViewRiskDetailsButtonClick(indicatorId: number, companyId: string) {
    this.dispatchEvent(
      new CustomEvent(RiskManagementSideMenuEvents.VIEW_MISCONFIGURATION_DETAILS, {
        detail: {
          companyId,
          indicatorId,
        },
        bubbles: true,
        cancelable: true,
        composed: true,
      })
    );
  }

  private handlePatchAppButtonClick(application: any) {
    const patchManagementDetails = this.patchManagementDetails[application['_id']];

    this.dispatchEvent(
      new CustomEvent(RiskManagementSideMenuEvents.PATCH_APP, {
        detail: {
          company: {
            id: this.item.company._id,
            name: this.item.company.name,
          },
          endpointId: this.item.endpointId,
          fullApplicationName: application.fullApplicationName,
          applicationIdToPatchIdMapping: patchManagementDetails.applicationIdToPatchIdMapping,
          patchesCount: patchManagementDetails.patchesCount,
          patchProductId: patchManagementDetails.patchProductId,
          patchProductName: patchManagementDetails.patchProductName,
          totalEndpoints: patchManagementDetails.totalEndpoints,
          patchableEndpoints: patchManagementDetails.patchableEndpoints,
          patchName: patchManagementDetails.patchName,
        },
        bubbles: true,
        cancelable: true,
        composed: false,
      })
    );
  }

  private composeViewDevicesByCveLink(application: any): string {
    const cves = application['cves'];
    const companyId = this.item.company._id;
    const filters = [
      {
        [DevicesColumnKeys.DEVICE_CVES]: {
          complexEnumFilter: cves.map(cve => {
            return {
              label: cve,
              value: cve,
            };
          }),
        },
      },
      {
        [DevicesColumnKeys.COMPANY]: {
          exactMatch: companyId,
        },
      },
    ];
    return buildHash('/risk-management-resources', { filters });
  }

  private handleFixRisksButtonClick() {
    this.dispatchEvent(
      new CustomEvent(RiskManagementSideMenuEvents.FIX_MISCONFIGURATIONS, {
        detail: {
          companyId: this.item.companyId,
          endpointsIds: [this.item.endpointId],
          hasFrat: this.item.hasFrat,
        },
        bubbles: true,
        cancelable: true,
        composed: true,
      })
    );
  }

  private getRollbackRisksButtonDisableState() {
    if (!this.item.company.specifics.features.iorUsable) {
      return {
        disabledRollback: true,
        tooltipRollback: translateFn('Your current license does not provide access to this feature.'),
      };
    }

    if (this.item.countRisksToRollback === 0 && isCloudResource(this.item.endpointType)) {
      return {
        disabledRollback: true,
        tooltipRollback: translateFn('Rolling back is not available for risks associated with cloud resources.'),
      };
    }

    if (this.item.countRisksToRollback === 0) {
      return {
        disabledRollback: true,
        tooltipRollback: translateFn('Rolling back is not available for any risks associated with this endpoint.'),
      };
    }

    return {
      disabledRollback: false,
    };
  }

  private handleRollbackFixesButtonClick(): void {
    this.dispatchEvent(
      new CustomEvent(RiskManagementSideMenuEvents.ROLLBACK_DEVICES, {
        detail: {
          endpointId: this.item.endpointId,
          endpointName: this.item.endpointName,
          companyId: this.item.company._id,
          hasFrat: this.item.hasFrat,
        },
        bubbles: true,
        cancelable: true,
        composed: false,
      })
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'devices-side-menu': DevicesSideMenuWebComponent;
  }
}
